import { type DocumentSection_Content, DocumentSectionType, type DocumentTemplate } from '@alpod-ai-writer/shared';
import { yTextToSlateElement } from '@slate-yjs/core';
import { useEffect,useState } from 'react';
import { Element, Node } from 'slate';
import * as Y from 'yjs';

import { isElementWithType } from '../utils/editor.utils';
import { useDocumentEditor } from './useDocumentEditor.hook';

export interface ToCSectionHeading {
    sectionId: string;
    sectionTitle: string;
    headingId?: string;
    headingTitle?: string;
    path: number[];
    level: number;
}

export interface ToCSection {
    sectionId: string;
    sectionTitle: string;
    headings: TocHeadingNode[];
}

export interface TocHeadingNode {
    heading: ToCSectionHeading;
    children: TocHeadingNode[];
  }

export function useTableOfContents() {
    const editor = useDocumentEditor();
    const document = editor.yjsProvider.document;

    const [ sections, setSections ] = useState(getSectionsFromDocument(editor.template, document));

    useEffect(() => {
        let debounceTimer: ReturnType<typeof setTimeout>|null = null;

        const handleUpdate = () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            debounceTimer = setTimeout(() => {
                const newState = getSectionsFromDocument(editor.template, document);
                setSections(newState);
            }, 300);
        };

        document.on('updateV2', handleUpdate);
        handleUpdate();

        return () => {
            document.off('updateV2', handleUpdate);
            
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    }, [ document ]);

    return { sections };
}

function getSectionsFromDocument(template: DocumentTemplate, document: Y.Doc) {
    let headings: ToCSectionHeading[] = [];
    const sections: ToCSection[] = [];

    const findHeadings = (sectionId: string, sectionTitle: string, path: number[], elements: unknown[]) => {
        elements.forEach((element, index) => {
            if (isElementWithType(element)) {
                const _path = [ ...path, index ];

                switch (element.type) {
                    case 'h1': {
                        headings.push({
                            sectionId,
                            sectionTitle,
                            headingId: element.id,
                            headingTitle: Node.string(element as Element),
                            level: 1,
                            path: _path
                        });
                        break;
                    }
                    case 'h-attachment': {
                        headings.push({
                            sectionId,
                            sectionTitle,
                            headingId: element.id,
                            headingTitle: Node.string(element as Element),
                            level: 4,
                            path: _path
                        });
                        break;
                    }
                    case 'h2': {
                        headings.push({
                            sectionId,
                            sectionTitle,
                            headingId: element.id,
                            headingTitle: Node.string(element as Element),
                            level: 2,
                            path: _path
                        });
                        break;
                    }
                    case 'h3': {
                        headings.push({
                            sectionId,
                            sectionTitle,
                            headingId: element.id,
                            headingTitle: Node.string(element as Element),
                            level: 3,
                            path: _path
                        });
                        break;
                    }
                    case 'h4': {
                        headings.push({
                            sectionId,
                            sectionTitle,
                            headingId: element.id,
                            headingTitle: Node.string(element as Element),
                            level: 4,
                            path: _path
                        });
                        break;
                    }
                    case 'h5': {
                        headings.push({
                            sectionId,
                            sectionTitle,
                            headingId: element.id,
                            headingTitle: Node.string(element as Element),
                            level: 5,
                            path: _path
                        });
                        break;
                    }
                }

                if (element.children?.length > 0) {
                    findHeadings(sectionId, sectionTitle, _path, element.children);
                }
            }
        });
    };

    template.config.sections
        .filter((section): section is DocumentSection_Content => section.type === DocumentSectionType.Content)
        .forEach(section => {
            if (!section.content) {
                return;
            }

            const docContent = document.get(section.content.id, Y.XmlText);

            if (docContent instanceof Y.XmlText) {
                const slateContent = yTextToSlateElement(docContent);

                const outSection: ToCSection = {
                    sectionId: section.id,
                    sectionTitle: section.title,
                    headings: []
                };

                sections.push(outSection);
                headings = [];

                findHeadings(section.id, section.title, [], slateContent.children);

                outSection.headings = buildHeadingTree(headings);
            }
        });

    return sections;
}

export function buildHeadingTree(headings: ToCSectionHeading[]): TocHeadingNode[] {
    const root: TocHeadingNode[] = [];
    const stack: TocHeadingNode[] = [];
  
    for (const heading of headings) {
        const node: TocHeadingNode = { heading, children: [] };
  
        // Remove nodes from the stack until we find one with a lower level.
        // This handles cases like an H3 without a proper H2 parent.
        while (stack.length > 0 && stack[stack.length - 1].heading.level >= heading.level) {
            stack.pop();
        }
  
        // If the stack is empty, no valid parent was found, so this node goes to the root.
        // Otherwise, it is added as a child of the current top of the stack.
        if (stack.length === 0) {
            root.push(node);
        } else {
            stack[stack.length - 1].children.push(node);
        }
  
        // Push the current node onto the stack.
        stack.push(node);
    }
  
    return root;
}
